import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { StepBodyProps } from 'v2/common/FormModalMultiStep/FormModalMultiStep';
import restFactory from 'v2/utils/restFactory';
import ErrorStep, { ErrorStepKey } from '../ErrorStep/ErrorStep';
import DRMBulkFileSelection from '../formSteps/FileSelection/FileSelection';
import HeadersMapping from '../formSteps/HeadersMapping/HeadersMapping';
import SuccessfulUploadMessage from '../SuccessfulUploadMessage/SuccessfulUploadMessage';
import {
  UseStepFlowProps,
  StepFlow,
  BulkImportFormData,
  BulkUploadFile,
} from '../types';
import { BulkImportType } from '../formSteps/FileSelection/hooks';

import { getContactListQueryKey } from 'v2/queries/useContacts';
import { getDataSetListQueryKey } from 'v2/queries/useDataSets';
import { getDataVendorsListQueryKey } from 'v2/queries/useDataVendors';

export const infiniteDRMEntitiesKeys = {
  data_vendor: getDataVendorsListQueryKey(),
  contact: getContactListQueryKey(),
  dataset: getDataSetListQueryKey(),
};

export function useBulkDRMEntityImportStepFlow({
  bulkImportType,
  onBulkImportTypeChange,
}: UseStepFlowProps): StepFlow<BulkImportFormData> {
  const queryClient = useQueryClient();
  const formContext = useForm<BulkImportFormData>({
    mode: 'onChange',
  });

  const { setValue } = formContext;

  useEffect(() => {
    setValue('bulkImportType', bulkImportType);
  }, [bulkImportType]);

  const {
    mutate: createBulkUploadFile,
    isLoading: isBulkUploadFileLoading,
    isSuccess: isBulkUploadFileSucess,
    data: bulkUploadedFile,
  } = useMutation(
    async (data: BulkImportFormData) => {
      const axiosOptions = {
        // content type to upload files in binary format
        'Content-Type': 'multipart/form-data',
      };

      const mapping = Object.entries(data.mapping)
        .map(([nomadColumn, fileColumn]) => {
          if (!fileColumn) {
            return null;
          }

          return {
            fileColumn,
            nomadColumn,
          };
        })
        .filter((relation) => relation);

      const payload = {
        drm_page: data.bulkImportType,
        file: data.file,
        column_mappings: JSON.stringify(mapping),
      };

      return await restFactory.post<BulkUploadFile>(
        `/dj/api/v1/bulk_upload_file/`,
        payload,
        axiosOptions
      );
    },
    {
      onError: () => {
        toast.error('Error uploading bulk data file.');
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          infiniteDRMEntitiesKeys[
            bulkImportType as Exclude<BulkImportType, BulkImportType.FILE>
          ]
        );
      },
    }
  );

  const missingColumnHeaders = ErrorStep({
    errorKey: ErrorStepKey.MissingColumnHeaders,
  });

  return {
    formContext,
    isSuccess: isBulkUploadFileSucess,
    isLoading: isBulkUploadFileLoading,
    onSubmit: createBulkUploadFile,
    steps: [
      {
        name: 'DRM flow - FileSelectionStep',
        body: function FileSelectionStep({ goToStep }: StepBodyProps) {
          return (
            <DRMBulkFileSelection
              bulkImportType={bulkImportType}
              isBulkUploadFileLoading={isBulkUploadFileLoading}
              goToStep={goToStep}
              onBulkImportTypeChange={onBulkImportTypeChange}
            />
          );
        },
      },
      {
        name: 'DRM flow - HeadersMappingStep',
        body: function HeadersMappingStep({ isStepActive }: StepBodyProps) {
          return (
            <HeadersMapping
              isBulkUploadFileLoading={isBulkUploadFileLoading}
              isStepActive={isStepActive}
            />
          );
        },
        next: {
          name: 'IMPORT',
          handler: 'submit',
        },
      },
      {
        name: 'DRM flow - SuccessfulUploadMessageStep',
        body: function SuccessfulUploadMessageStep() {
          return (
            <SuccessfulUploadMessage bulkUploadedFile={bulkUploadedFile} />
          );
        },
        next: {
          name: 'DONE',
          handler: 'close',
        },
        cancel: {
          handler: 'close',
          show: false,
        },
        previous: {
          show: false,
        },
      },
    ],
    stepErrors: [
      {
        name: 'MissingColumnHeaders',
        body: () => missingColumnHeaders.body,
        header: missingColumnHeaders.header,
      },
      {
        name: 'MissingRequiredHeaders',
        body: () =>
          ErrorStep({ errorKey: ErrorStepKey.MissingRequiredHeaders }).body,
      },
      {
        name: 'RowsWithoutSameColumns',
        body: () =>
          ErrorStep({ errorKey: ErrorStepKey.RowsWithoutSameColumns }).body,
      },
      {
        name: 'EmptyHeaders',
        body: () => ErrorStep({ errorKey: ErrorStepKey.EmptyHeaders }).body,
      },
      {
        name: 'RepeatedHeaders',
        body: () => ErrorStep({ errorKey: ErrorStepKey.RepeatedHeaders }).body,
      },
    ],
  };
}
